import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  DollarSign,
  Calendar,
  Star,
  Package,
  Banknote,
  Wallet,
  ArrowUpRight,
  History,
  Trash2,
  RefreshCw,
} from 'lucide-react';
import { StatsGrid } from '../../admin';
import {
  getAdminVendorDisbursements,
  deleteAdminVendorDisbursement,
} from '../../../services/vendorPayoutService';
import { formatDateTime } from '../../../utils/dateUtils';
import { useToastContext } from '../../../contexts/ToastContext';

const toNumber = (v, fallback = 0) => {
  if (v == null || v === '') return fallback;
  const n = typeof v === 'number' ? v : parseFloat(String(v), 10);
  return Number.isFinite(n) ? n : fallback;
};

const AnalyticsTab = ({ vendor, vendorProfile }) => {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToastContext();
  const vendorProfileId = vendorProfile?.id;
  const [deleteTarget, setDeleteTarget] = useState(null);

  /**
   * Admin vendor API (`AdminVendorController@show`) loads profile with:
   * - `withSum(..., 'vendor_payout_amount')` → attribute `total_revenue` (completed bookings)
   * - `withCount('bookings')` → `bookings_count`
   * - `payout_ledger` → summary from `VendorPayoutLedgerService` (total_earned_from_orders, total_disbursed, available_balance)
   */
  const totalEarnings = useMemo(() => {
    const raw =
      vendorProfile?.payout_ledger?.total_earned_from_orders ??
      vendorProfile?.total_earned_from_orders ??
      vendorProfile?.total_revenue ??
      vendorProfile?.totalRevenue ??
      vendorProfile?.total_earnings ??
      vendorProfile?.totalEarnings ??
      vendor?.total_earnings ??
      vendor?.totalEarnings ??
      0;
    return toNumber(raw, 0);
  }, [vendor, vendorProfile]);

  const totalPayoutsSent = useMemo(() => {
    const raw =
      vendorProfile?.payout_ledger?.total_disbursed ??
      vendorProfile?.total_payouts_sent ??
      vendorProfile?.total_disbursed ??
      vendor?.total_payouts_sent ??
      vendor?.total_disbursed ??
      0;
    return toNumber(raw, 0);
  }, [vendor, vendorProfile]);

  const payoutDue = useMemo(() => {
    const raw =
      vendorProfile?.payout_ledger?.available_balance ??
      vendorProfile?.payout_available_balance ??
      vendorProfile?.available_balance;
    if (raw != null && raw !== '') {
      const n = toNumber(raw, NaN);
      if (Number.isFinite(n)) return n;
    }
    return Math.max(0, totalEarnings - totalPayoutsSent);
  }, [vendorProfile, totalEarnings, totalPayoutsSent]);

  const totalBookings = useMemo(() => {
    const raw =
      vendorProfile?.bookings_count ??
      vendorProfile?.bookingsCount ??
      vendorProfile?.total_bookings ??
      vendorProfile?.totalBookings ??
      vendor?.total_bookings ??
      vendor?.totalBookings;
    if (raw != null && raw !== '') {
      const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
      if (Number.isFinite(n)) return n;
    }
    return vendor?.bookings?.length ?? 0;
  }, [vendor, vendorProfile]);

  const averageRating = vendorProfile?.average_rating ?? vendorProfile?.averageRating ?? 0;
  const totalReviews = vendorProfile?.total_reviews ?? vendorProfile?.totalReviews ?? vendor?.reviews?.length ?? 0;
  const totalServices = vendorProfile?.vendor_services?.length || vendorProfile?.vendorServices?.length || 0;
  const totalPackages =
    vendorProfile?.vendor_service_packages?.length || vendorProfile?.vendorServicePackages?.length || 0;

  // Fetch recent payout disbursements for this vendor
  const {
    data: disbursementsResponse,
    isLoading: disbursementsLoading,
    refetch: refetchDisbursements,
  } = useQuery({
    queryKey: ['admin-vendor-disbursements-tab', vendorProfileId],
    queryFn: () => getAdminVendorDisbursements(vendorProfileId, { per_page: 5 }),
    enabled: Boolean(vendorProfileId),
    staleTime: 30000,
  });

  const recentDisbursements = disbursementsResponse?.data?.data || [];

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (disbursementId) => {
      if (!vendorProfileId) throw new Error('No vendor profile ID');
      return deleteAdminVendorDisbursement(vendorProfileId, disbursementId);
    },
    onSuccess: (res) => {
      success(res?.message || 'Disbursement record deleted.');
      queryClient.invalidateQueries({ queryKey: ['admin-vendor-disbursements-tab', vendorProfileId] });
      queryClient.invalidateQueries({ queryKey: ['admin-vendor-payout-vendors'] });
      queryClient.invalidateQueries({ queryKey: ['vendor', String(vendor?.id || vendorProfile?.user_id)] });
      refetchDisbursements();
      setDeleteTarget(null);
    },
    onError: (err) => {
      showError(err?.message || 'Could not delete disbursement.');
    },
  });

  const formatCurrency = (amount) => {
    const n = toNumber(amount, NaN);
    if (Number.isFinite(n)) {
      return `$${n.toFixed(2)}`;
    }
    return '$0.00';
  };

  const formatRating = (rating) => {
    if (rating && rating > 0) {
      return `${Number(rating).toFixed(1)}/5.0`;
    }
    return 'N/A';
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-base sm:text-lg font-semibold text-gray-900">Vendor Analytics</h3>
        <p className="text-sm text-gray-600 mt-1">Overview of vendor performance, revenue, and payouts</p>
      </div>

      <StatsGrid 
        stats={[
          {
            key: 'earnings',
            title: 'Total Earnings',
            value: formatCurrency(totalEarnings),
            icon: DollarSign,
            variant: 'success'
          },
          {
            key: 'payouts_sent',
            title: 'Total Payouts Sent',
            value: formatCurrency(totalPayoutsSent),
            icon: Banknote,
            variant: 'info'
          },
          {
            key: 'payout_due',
            title: 'Payout Due',
            value: formatCurrency(payoutDue),
            icon: Wallet,
            variant: payoutDue > 0 ? 'warning' : 'default'
          },
          {
            key: 'bookings',
            title: 'Total Bookings',
            value: totalBookings.toString(),
            icon: Calendar,
            variant: 'info'
          },
          {
            key: 'rating',
            title: 'Average Rating',
            value: formatRating(averageRating),
            icon: Star,
            variant: 'warning'
          },
          {
            key: 'reviews',
            title: 'Total Reviews',
            value: totalReviews.toString(),
            icon: Star,
            variant: 'default'
          },
          {
            key: 'services',
            title: 'Services',
            value: totalServices.toString(),
            icon: Package,
            variant: 'info'
          },
          {
            key: 'packages',
            title: 'Packages',
            value: totalPackages.toString(),
            icon: Package,
            variant: 'default'
          }
        ]}
        columns={4}
      />

      {/* Payout & Ledger Details Section */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <div className="p-5 sm:p-6 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-start sm:items-center gap-3">
            <div className="p-2.5 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-100">
              <Banknote className="w-6 h-6" />
            </div>
            <div>
              <h4 className="text-base font-semibold text-gray-900">Vendor Payouts & Ledger</h4>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                Total payout amounts recorded and sent to this vendor by admin
              </p>
            </div>
          </div>
          <Link
            to={`/admin/vendor-payouts?vendor_id=${vendorProfileId}`}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-black text-white text-xs sm:text-sm font-medium hover:bg-gray-800 transition-colors w-fit"
          >
            <span>Manage Vendor Payouts</span>
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="p-5 sm:p-6 space-y-6">
          {/* Payout Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 rounded-xl bg-gray-50 border border-gray-200">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Earned from Orders</span>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">
                {formatCurrency(totalEarnings)}
              </p>
              <p className="text-xs text-gray-500 mt-1">Vendor share from completed bookings</p>
            </div>

            <div className="p-4 rounded-xl bg-blue-50 border border-blue-200">
              <span className="text-xs font-medium text-blue-700 uppercase tracking-wider">Total Sent to Vendor</span>
              <p className="text-xl sm:text-2xl font-bold text-blue-900 mt-1">
                {formatCurrency(totalPayoutsSent)}
              </p>
              <p className="text-xs text-blue-700 mt-1">Disbursements recorded by admin</p>
            </div>

            <div className={`p-4 rounded-xl border ${payoutDue > 0 ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
              <span className={`text-xs font-medium uppercase tracking-wider ${payoutDue > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                {payoutDue > 0 ? 'Balance Due to Pay' : 'Payout Status'}
              </span>
              <p className={`text-xl sm:text-2xl font-bold mt-1 ${payoutDue > 0 ? 'text-amber-900' : 'text-emerald-900'}`}>
                {formatCurrency(payoutDue)}
              </p>
              <p className={`text-xs mt-1 ${payoutDue > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                {payoutDue > 0 ? 'Available balance to disburse' : 'All earnings fully disbursed'}
              </p>
            </div>
          </div>

          {/* Recent Payout Disbursements List */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h5 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                <History className="w-4 h-4 text-gray-500" />
                <span>Recent Disbursements</span>
              </h5>
              <Link
                to={`/admin/vendor-payouts?vendor_id=${vendorProfileId}`}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium hover:underline"
              >
                View all in payouts &rarr;
              </Link>
            </div>

            {disbursementsLoading ? (
              <div className="py-6 text-center text-sm text-gray-500">
                Loading payout history...
              </div>
            ) : recentDisbursements.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center">
                <p className="text-sm text-gray-500">No payout disbursements recorded yet for this vendor.</p>
                <Link
                  to={`/admin/vendor-payouts?vendor_id=${vendorProfileId}`}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:underline"
                >
                  Record first payout on Vendor Payouts page
                </Link>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-left text-xs sm:text-sm">
                    <thead className="bg-gray-50 text-gray-600 font-medium">
                      <tr>
                        <th className="px-4 py-2.5">Reference</th>
                        <th className="px-4 py-2.5">Amount</th>
                        <th className="px-4 py-2.5">Date</th>
                        <th className="px-4 py-2.5">Booking</th>
                        <th className="px-4 py-2.5">Notes</th>
                        <th className="px-4 py-2.5">Recorded By</th>
                        <th className="px-4 py-2.5 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {recentDisbursements.map((d) => (
                        <tr key={d.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-mono font-medium text-gray-900">
                            {d.disbursement_reference}
                          </td>
                          <td className="px-4 py-3 font-semibold text-emerald-700">
                            {formatCurrency(d.amount)}
                          </td>
                          <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                            {formatDateTime(d.created_at)}
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {d.booking?.booking_reference ? (
                              <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-xs">
                                {d.booking.booking_reference}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-600 max-w-xs truncate">
                            {d.notes || '—'}
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-xs">
                            {d.recorded_by?.name || d.recorded_by?.email || 'Admin'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              type="button"
                              onClick={() => setDeleteTarget(d)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 border border-transparent hover:border-red-100 transition-colors"
                              title="Delete payout record"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              <span>Delete</span>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center gap-3 text-red-600">
              <div className="p-3 bg-red-100 rounded-xl">
                <Trash2 className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Delete Disbursement</h3>
                <p className="text-xs text-gray-500">This action will remove this payout entry.</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-3.5 border border-gray-200 text-xs space-y-1.5">
              <p>
                <strong>Reference:</strong>{' '}
                <span className="font-mono">{deleteTarget.disbursement_reference}</span>
              </p>
              <p>
                <strong>Amount:</strong>{' '}
                <span className="font-bold text-emerald-700">
                  {formatCurrency(deleteTarget.amount)}
                </span>
              </p>
              {deleteTarget.notes && (
                <p className="truncate">
                  <strong>Notes:</strong> {deleteTarget.notes}
                </p>
              )}
            </div>

            <p className="text-xs text-gray-600">
              Deleting this disbursement will update the vendor’s ledger and restore{' '}
              <strong>{formatCurrency(deleteTarget.amount)}</strong> to their balance due.
            </p>

            <div className="flex gap-2.5 justify-end pt-3 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 rounded-xl border border-gray-300 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => deleteMutation.mutate(deleteTarget.id)}
                disabled={deleteMutation.isPending}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-red-600 text-white text-xs font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors shadow-sm"
              >
                {deleteMutation.isPending && (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                )}
                {deleteMutation.isPending ? 'Deleting…' : 'Confirm Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsTab;
